import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { imgUrl } from '../utils/imgUrl';
import { religions, castes, stars, raasis, motherTongues, countries, indianStates, occupationCategories, familyTypes, familyStatuses, familyValues, annualIncomeRanges } from '../data/profileOptions';
import MultiSelect from '../components/MultiSelect';
import './EditProfile.css';

const TABS = ['Basic', 'Religious', 'Professional', 'Location', 'Family', 'Lifestyle', 'Photos', 'Partner Preferences', 'Kundli'];

const EditProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = TABS.findIndex(t => t.toLowerCase() === (searchParams.get('tab') || '').toLowerCase());
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : 0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [kundli, setKundli] = useState({ chart: null, birth_time: '', birth_latitude: '', birth_longitude: '' });
  const [generating, setGenerating] = useState(false);

  const [basic, setBasic] = useState({ first_name: '', last_name: '', height: '', weight: '', marital_status: '', mother_tongue: '', about_me: '' });
  const [religious, setReligious] = useState({ religion: '', caste: '', sub_caste: '', gotra: '', star: '', raasi: '' });
  const [professional, setProfessional] = useState({ occupation: '', occupation_category: '', company_name: '', annual_income: '', work_location: '' });
  const [location, setLocation] = useState({ country: '', state: '', city: '', zip_code: '' });
  const [family, setFamily] = useState({ father_name: '', father_occupation: '', mother_name: '', mother_occupation: '', siblings: '', family_type: '', family_status: '', family_values: '' });
  const [lifestyle, setLifestyle] = useState({ diet: '', smoking: '', drinking: '', hobbies: '', interests: '' });

  useEffect(() => {
    authService.getEditProfile().then(res => {
      const p = res.data;
      setBasic({ first_name: p.first_name || '', last_name: p.last_name || '', height: p.height || '', weight: p.weight || '', marital_status: p.marital_status || '', mother_tongue: p.mother_tongue || '', about_me: p.about_me || '' });
      if (p.religious_details) setReligious({ religion: p.religious_details.religion || '', caste: p.religious_details.caste || '', sub_caste: p.religious_details.sub_caste || '', gotra: p.religious_details.gotra || '', star: p.religious_details.star || '', raasi: p.religious_details.raasi || '' });
      if (p.professional_details) setProfessional({ occupation: p.professional_details.occupation || '', occupation_category: p.professional_details.occupation_category || '', company_name: p.professional_details.company_name || '', annual_income: p.professional_details.annual_income || '', work_location: p.professional_details.work_location || '' });
      if (p.location) setLocation({ country: p.location.country || '', state: p.location.state || '', city: p.location.city || '', zip_code: p.location.zip_code || '' });
      if (p.family_details) setFamily({ father_name: p.family_details.father_name || '', father_occupation: p.family_details.father_occupation || '', mother_name: p.family_details.mother_name || '', mother_occupation: p.family_details.mother_occupation || '', siblings: p.family_details.siblings || '', family_type: p.family_details.family_type || '', family_status: p.family_details.family_status || '', family_values: p.family_details.family_values || '' });
      if (p.lifestyle) setLifestyle({ diet: p.lifestyle.diet || '', smoking: p.lifestyle.smoking || '', drinking: p.lifestyle.drinking || '', hobbies: p.lifestyle.hobbies || '', interests: p.lifestyle.interests || '' });
      if (p.photos) setPhotos(p.photos);
    }).finally(() => setLoading(false));
    // Load partner preferences
    authService.getPartnerPreferences().then(res => setPreferences(res.data || {})).catch(() => {});
    // Load kundli data
    authService.getKundli().then(res => setKundli(res.data || {})).catch(() => {});
  }, []);

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await authService.uploadPhoto(formData);
      setPhotos(prev => [...prev, res.data.photo]);
      setSuccess('Photo uploaded!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await authService.deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      setError('Failed to delete photo');
    }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      await authService.setPrimaryPhoto(photoId);
      setPhotos(prev => prev.map(p => ({ ...p, is_primary: p.id === photoId })));
      setSuccess('Profile photo updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to set primary photo');
    }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = { ...basic };
      if (Object.values(religious).some(v => v)) payload.religious = religious;
      if (Object.values(professional).some(v => v)) payload.professional = professional;
      if (Object.values(location).some(v => v)) payload.location = location;
      if (Object.values(family).some(v => v)) payload.family = family;
      if (Object.values(lifestyle).some(v => v)) payload.lifestyle = lifestyle;
      await authService.updateProfile(payload);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, value, onChange, type = 'text') => (
    <div className="ep-field">
      <label>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );

  const select = (label, value, onChange, options) => (
    <div className="ep-field">
      <label>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">Select</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const textarea = (label, value, onChange) => (
    <div className="ep-field ep-field-full">
      <label>{label}</label>
      <textarea rows={4} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );

  if (loading) return <div className="ep-loading">Loading profile...</div>;

  return (
    <div className="ep-wrapper">
      <div className="ep-header">
        <button className="ep-back" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2>Edit Profile</h2>
        <button className="ep-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {success && <div className="ep-success">{success}</div>}
      {error && <div className="ep-error">{error}</div>}

      <div className="ep-tabs">
        {TABS.map((tab, i) => (
          <button key={tab} className={`ep-tab ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
            {tab}
          </button>
        ))}
      </div>

      <div className="ep-body">
        {activeTab === 0 && (
          <div className="ep-grid">
            {field('First Name', basic.first_name, v => setBasic({ ...basic, first_name: v }))}
            {field('Last Name', basic.last_name, v => setBasic({ ...basic, last_name: v }))}
            {field('Height (cm)', basic.height, v => setBasic({ ...basic, height: v }), 'number')}
            {field('Weight (kg)', basic.weight, v => setBasic({ ...basic, weight: v }), 'number')}
            {select('Mother Tongue', basic.mother_tongue, v => setBasic({ ...basic, mother_tongue: v }), motherTongues.map(m => ({ value: m, label: m })))}
            {select('Marital Status', basic.marital_status, v => setBasic({ ...basic, marital_status: v }), [
              { value: 'never_married', label: 'Never Married' },
              { value: 'divorced', label: 'Divorced' },
              { value: 'widowed', label: 'Widowed' },
              { value: 'separated', label: 'Separated' },
            ])}
            {textarea('About Me', basic.about_me, v => setBasic({ ...basic, about_me: v }))}
          </div>
        )}

        {activeTab === 1 && (
          <div className="ep-grid">
            {select('Religion', religious.religion, v => setReligious({ ...religious, religion: v, caste: '' }), religions.map(r => ({ value: r, label: r })))}
            {select('Caste', religious.caste, v => setReligious({ ...religious, caste: v }), (castes[religious.religion] || []).map(c => ({ value: c, label: c })))}
            {field('Sub Caste', religious.sub_caste, v => setReligious({ ...religious, sub_caste: v }))}
            {field('Gotra', religious.gotra, v => setReligious({ ...religious, gotra: v }))}
            {select('Star (Nakshatra)', religious.star, v => setReligious({ ...religious, star: v }), stars.map(s => ({ value: s, label: s })))}
            {select('Raasi', religious.raasi, v => setReligious({ ...religious, raasi: v }), raasis.map(r => ({ value: r, label: r })))}
          </div>
        )}

        {activeTab === 2 && (
          <div className="ep-grid">
            {field('Occupation', professional.occupation, v => setProfessional({ ...professional, occupation: v }))}
            {select('Occupation Category', professional.occupation_category, v => setProfessional({ ...professional, occupation_category: v }), occupationCategories)}
            {field('Company Name', professional.company_name, v => setProfessional({ ...professional, company_name: v }))}
            {select('Annual Income', professional.annual_income, v => setProfessional({ ...professional, annual_income: v }), annualIncomeRanges)}
            {field('Work Location', professional.work_location, v => setProfessional({ ...professional, work_location: v }))}
          </div>
        )}

        {activeTab === 3 && (
          <div className="ep-grid">
            {select('Country', location.country, v => setLocation({ ...location, country: v }), countries.map(c => ({ value: c, label: c })))}
            {select('State', location.state, v => setLocation({ ...location, state: v }), indianStates.map(s => ({ value: s, label: s })))}
            {field('City', location.city, v => setLocation({ ...location, city: v }))}
            {field('ZIP Code', location.zip_code, v => setLocation({ ...location, zip_code: v }))}
          </div>
        )}

        {activeTab === 4 && (
          <div className="ep-grid">
            {field("Father's Name", family.father_name, v => setFamily({ ...family, father_name: v }))}
            {field("Father's Occupation", family.father_occupation, v => setFamily({ ...family, father_occupation: v }))}
            {field("Mother's Name", family.mother_name, v => setFamily({ ...family, mother_name: v }))}
            {field("Mother's Occupation", family.mother_occupation, v => setFamily({ ...family, mother_occupation: v }))}
            {field('No. of Siblings', family.siblings, v => setFamily({ ...family, siblings: v }), 'number')}
            {select('Family Type', family.family_type, v => setFamily({ ...family, family_type: v }), familyTypes)}
            {select('Family Status', family.family_status, v => setFamily({ ...family, family_status: v }), familyStatuses)}
            {select('Family Values', family.family_values, v => setFamily({ ...family, family_values: v }), familyValues)}
          </div>
        )}

        {activeTab === 5 && (
          <div className="ep-grid">
            {select('Diet', lifestyle.diet, v => setLifestyle({ ...lifestyle, diet: v }), [
              { value: 'vegetarian', label: 'Vegetarian' },
              { value: 'non_vegetarian', label: 'Non Vegetarian' },
              { value: 'eggetarian', label: 'Eggetarian' },
              { value: 'vegan', label: 'Vegan' },
            ])}
            {select('Smoking', lifestyle.smoking, v => setLifestyle({ ...lifestyle, smoking: v }), [
              { value: 'no', label: 'No' },
              { value: 'occasionally', label: 'Occasionally' },
              { value: 'yes', label: 'Yes' },
            ])}
            {select('Drinking', lifestyle.drinking, v => setLifestyle({ ...lifestyle, drinking: v }), [
              { value: 'no', label: 'No' },
              { value: 'occasionally', label: 'Occasionally' },
              { value: 'yes', label: 'Yes' },
            ])}
            {textarea('Hobbies', lifestyle.hobbies, v => setLifestyle({ ...lifestyle, hobbies: v }))}
            {textarea('Interests', lifestyle.interests, v => setLifestyle({ ...lifestyle, interests: v }))}
          </div>
        )}
        {activeTab === 6 && (
          <div className="ep-photos-section">
            <div className="ep-photo-upload">
              <label className={`ep-upload-btn ${uploading ? 'uploading' : ''}`}>
                {uploading ? 'Uploading...' : '+ Add Photo'}
                <input type="file" accept="image/*" onChange={handleUploadPhoto} disabled={uploading} hidden />
              </label>
              <p className="ep-upload-hint">JPG, PNG or WEBP · Max 2MB · Up to 10 photos</p>
            </div>
            {photos.length === 0 ? (
              <div className="ep-no-photos">No photos uploaded yet</div>
            ) : (
              <div className="ep-photos-grid">
                {photos.map(photo => (
                  <div key={photo.id} className={`ep-photo-item ${photo.is_primary ? 'primary' : ''}`}>
                    <img src={imgUrl(photo.photo_url)} alt="profile" />
                    {photo.is_primary && <span className="ep-primary-badge">✓ Primary</span>}
                    <div className="ep-photo-actions">
                      {!photo.is_primary && (
                        <button onClick={() => handleSetPrimary(photo.id)} title="Set as primary">⭐ Set Primary</button>
                      )}
                      <button onClick={() => handleDeletePhoto(photo.id)} className="ep-delete-btn" title="Delete">🗑 Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 7 && (
          <div className="ep-grid">
            <div className="ep-field">
              <label>Age From</label>
              <input type="number" min="18" max="80" value={preferences.age_from || ''} onChange={e => setPreferences({ ...preferences, age_from: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div className="ep-field">
              <label>Age To</label>
              <input type="number" min="18" max="80" value={preferences.age_to || ''} onChange={e => setPreferences({ ...preferences, age_to: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div className="ep-field">
              <label>Height From (cm)</label>
              <input type="number" min="100" max="250" value={preferences.height_from || ''} onChange={e => setPreferences({ ...preferences, height_from: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div className="ep-field">
              <label>Height To (cm)</label>
              <input type="number" min="100" max="250" value={preferences.height_to || ''} onChange={e => setPreferences({ ...preferences, height_to: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <MultiSelect label="Marital Status" options={['never_married', 'divorced', 'widowed', 'separated']} selected={preferences.marital_status || []} onChange={v => setPreferences({ ...preferences, marital_status: v })} />
            <MultiSelect label="Religion" options={religions} selected={preferences.religion || []} onChange={v => setPreferences({ ...preferences, religion: v, caste: [] })} />
            <MultiSelect label="Caste" options={(preferences.religion || []).flatMap(r => castes[r] || [])} selected={preferences.caste || []} onChange={v => setPreferences({ ...preferences, caste: v })} />
            <MultiSelect label="Mother Tongue" options={motherTongues} selected={preferences.mother_tongue || []} onChange={v => setPreferences({ ...preferences, mother_tongue: v })} />
            <MultiSelect label="Occupation Category" options={occupationCategories.map(o => o.value)} selected={preferences.occupation_category || []} onChange={v => setPreferences({ ...preferences, occupation_category: v })} />
            {select('Annual Income', preferences.annual_income, v => setPreferences({ ...preferences, annual_income: v }), annualIncomeRanges)}
            <MultiSelect label="Country" options={countries} selected={preferences.country || []} onChange={v => setPreferences({ ...preferences, country: v })} />
            <MultiSelect label="State" options={indianStates} selected={preferences.state || []} onChange={v => setPreferences({ ...preferences, state: v })} />
            {field('City', preferences.city, v => setPreferences({ ...preferences, city: v }))}
            {select('Diet', preferences.diet, v => setPreferences({ ...preferences, diet: v }), [
              { value: 'vegetarian', label: 'Vegetarian' }, { value: 'non_vegetarian', label: 'Non Vegetarian' },
              { value: 'eggetarian', label: 'Eggetarian' }, { value: 'vegan', label: 'Vegan' },
            ])}
            {select('Smoking', preferences.smoking, v => setPreferences({ ...preferences, smoking: v }), [
              { value: 'no', label: 'No' }, { value: 'occasionally', label: 'Occasionally' }, { value: 'yes', label: 'Yes' },
            ])}
            {select('Drinking', preferences.drinking, v => setPreferences({ ...preferences, drinking: v }), [
              { value: 'no', label: 'No' }, { value: 'occasionally', label: 'Occasionally' }, { value: 'yes', label: 'Yes' },
            ])}
            {select('Family Type', preferences.family_type, v => setPreferences({ ...preferences, family_type: v }), familyTypes)}
            {select('Family Values', preferences.family_values, v => setPreferences({ ...preferences, family_values: v }), familyValues)}
            <MultiSelect label="Star (Nakshatra)" options={stars} selected={preferences.star || []} onChange={v => setPreferences({ ...preferences, star: v })} />
            <MultiSelect label="Raasi" options={raasis} selected={preferences.raasi || []} onChange={v => setPreferences({ ...preferences, raasi: v })} />
            <div className="ep-field ep-field-full">
              <button type="button" className="ep-save" onClick={async () => {
                setSaving(true); setError(''); setSuccess('');
                try {
                  await authService.savePartnerPreferences(preferences);
                  setSuccess('Partner preferences saved!');
                  setTimeout(() => setSuccess(''), 3000);
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to save preferences');
                } finally { setSaving(false); }
              }} disabled={saving}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        )}
        {activeTab === 8 && (
          <div className="ep-grid">
            <div className="ep-field">
              <label>Birth Time</label>
              <input type="time" value={kundli.birth_time || ''} onChange={e => setKundli({ ...kundli, birth_time: e.target.value })} />
            </div>
            <div className="ep-field ep-field-full">
              <button type="button" className="ep-save" onClick={async () => {
                setSaving(true); setError(''); setSuccess('');
                try {
                  await authService.saveBirthDetails({
                    birth_time: kundli.birth_time,
                  });
                  setSuccess('Birth details saved!');
                  setTimeout(() => setSuccess(''), 3000);
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to save birth details');
                } finally { setSaving(false); }
              }} disabled={saving}>
                {saving ? 'Saving...' : 'Save Birth Details'}
              </button>
            </div>

            <div className="ep-field ep-field-full" style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 8 }}>
              <label style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, display: 'block' }}>Kundli Chart</label>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <button type="button" className="ep-save" onClick={async () => {
                  setGenerating(true); setError(''); setSuccess('');
                  try {
                    const res = await authService.generateKundli();
                    setKundli(prev => ({ ...prev, chart: res.data.chart }));
                    setSuccess('Kundli chart generated!');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err) {
                    setError(err.response?.data?.message || 'Failed to generate kundli');
                  } finally { setGenerating(false); }
                }} disabled={generating}>
                  {generating ? 'Generating...' : '🔮 Generate Kundli Chart'}
                </button>
                <label className="ep-upload-btn" style={{ cursor: 'pointer' }}>
                  📤 Upload Chart Image
                  <input type="file" accept="image/*" hidden onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setError(''); setSuccess('');
                    const formData = new FormData();
                    formData.append('chart_image', file);
                    try {
                      const res = await authService.uploadKundli(formData);
                      setKundli(prev => ({ ...prev, chart: res.data.chart }));
                      setSuccess('Kundli chart uploaded!');
                      setTimeout(() => setSuccess(''), 3000);
                    } catch (err) {
                      setError(err.response?.data?.message || 'Upload failed');
                    }
                    e.target.value = '';
                  }} />
                </label>
              </div>

              {kundli.chart && (
                <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  {kundli.chart.source === 'generated' && kundli.chart.chart_svg ? (
                    <div dangerouslySetInnerHTML={{ __html: kundli.chart.chart_svg }} style={{ maxWidth: 400, margin: '0 auto' }} />
                  ) : kundli.chart.chart_image ? (
                    <img src={`http://localhost:8000${kundli.chart.chart_image}`} alt="Kundli Chart" style={{ maxWidth: 400, borderRadius: 8 }} />
                  ) : (
                    <p style={{ color: '#999' }}>No chart available</p>
                  )}
                  <p style={{ fontSize: '0.8rem', color: '#888', marginTop: 8 }}>
                    Source: {kundli.chart.source === 'generated' ? 'Generated via Prokerala API' : 'Uploaded'}
                  </p>
                </div>
              )}
              {!kundli.chart && (
                <p style={{ color: '#999', fontSize: '0.9rem' }}>No kundli chart yet. Save birth details first, then generate or upload.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditProfile;
